import { useEffect, useMemo } from "react";

import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";

interface WorkspaceTabLike {
  label: string;
  path: string;
}

interface UseWorkspaceChromeLoggingArgs<TTab extends WorkspaceTabLike> {
  activePath: string;
  componentName: string;
  pathname: string;
  tabs: readonly TTab[];
  workspaceLabel: string;
}

/**
 * Shared debug logging for route-backed workspace tab bars.
 *
 * @remarks
 * Preview investigations repeatedly need to answer two questions:
 * 1. which tab did the shell think was active for the current pathname?
 * 2. which tab path did the operator actually click?
 *
 * Keep this metadata-only. Paths are internal admin routes, and labels/counts
 * are safe to log at `debug` when preview diagnostics are enabled.
 */
export const useWorkspaceChromeLogging = <TTab extends WorkspaceTabLike>({
  activePath,
  componentName,
  pathname,
  tabs,
  workspaceLabel,
}: UseWorkspaceChromeLoggingArgs<TTab>) => {
  const logger = useMemo(
    () => FrontendLoggerFactory.getLogger().withComponent(componentName),
    [componentName],
  );

  useEffect(() => {
    const matchedTab = tabs.find((tab) => tab.path === activePath) ?? null;

    logger.debug("Workspace tab resolution updated", {
      operation: "workspace_tab_resolution",
      workspaceLabel,
      pathname,
      activePath,
      matchedTabLabel: matchedTab?.label ?? null,
      matchedTabPath: matchedTab?.path ?? null,
      tabCount: tabs.length,
      usedFallbackDefault: matchedTab === null,
    });
  }, [activePath, logger, pathname, tabs, workspaceLabel]);

  return (tab: TTab) => {
    logger.debug("Workspace tab navigation selected", {
      operation: "workspace_tab_navigation_click",
      workspaceLabel,
      pathname,
      activePath,
      targetTabLabel: tab.label,
      targetTabPath: tab.path,
      isCurrentTab: tab.path === activePath,
    });
  };
};
